/**
 * Accounting · Cost per Pharmacy
 *
 * For a date range, shows what we spend at each fulfilling pharmacy: drug COGS
 * (curated price, pharmacy-matched — same basis as the P&L report) plus per-order
 * shipping. Orders not yet routed to a pharmacy are excluded.
 *
 * - Single clinic: per-pharmacy table + summary cards + daily spend trend
 * - All Clinics (aggregate): pharmacies rolled up by name with a per-clinic split
 */

import { useState, useEffect, useCallback, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import { format, parse } from 'date-fns'
import { Card, CardHeader, CardTitle, CardContent, Button } from '../components/ui'
import { DateRangePicker } from '../components/DateRangePicker'
import { accountingAPI } from '../services/api'
import { useAuthStore } from '../stores/authStore'
import DataFreshnessWarning from '../components/DataFreshnessWarning'
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts'
import {
  DollarSign,
  Package,
  Truck,
  Building2,
  RefreshCw,
  AlertCircle,
  Loader2,
} from 'lucide-react'

const AUTO_REFRESH_INTERVAL = 15 * 60 * 1000 // 15 minutes

const getDefaultDateRange = () => {
  const end = new Date()
  const start = new Date()
  start.setDate(start.getDate() - 29)
  return { from: start, to: end }
}

function formatCurrency(cents, compact = false) {
  const dollars = (cents || 0) / 100
  if (compact && Math.abs(dollars) >= 1000) {
    return new Intl.NumberFormat('en-US', {
      style: 'currency', currency: 'USD', notation: 'compact', maximumFractionDigits: 1
    }).format(dollars)
  }
  return new Intl.NumberFormat('en-US', {
    style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0
  }).format(dollars)
}

function formatNumber(num) {
  return new Intl.NumberFormat('en-US').format(num || 0)
}

function SummaryCard({ title, value, subtitle, icon: Icon, loading }) {
  return (
    <Card className="relative overflow-hidden">
      <CardContent className="p-6">
        {loading ? (
          <div className="animate-pulse space-y-3">
            <div className="h-4 bg-muted rounded w-1/2"></div>
            <div className="h-8 bg-muted rounded w-3/4"></div>
            <div className="h-3 bg-muted rounded w-1/3"></div>
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-muted-foreground">{title}</span>
              {Icon && (
                <div className="p-2 bg-primary/10 rounded-lg">
                  <Icon className="h-4 w-4 text-primary" />
                </div>
              )}
            </div>
            <div className="flex items-center gap-2">
              <span className="text-2xl font-bold tracking-tight">{value}</span>
            </div>
            {subtitle && (
              <div className="flex items-center gap-2 mt-1">
                <span className="text-xs text-muted-foreground">{subtitle}</span>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  )
}

function CostTooltip({ active, payload, label }) {
  if (!active || !payload || !payload.length) return null
  return (
    <div className="bg-popover border border-border rounded-lg shadow-lg p-3 min-w-[180px]">
      <p className="font-medium text-sm mb-2">{label}</p>
      <div className="space-y-1">
        {payload.map((entry, index) => (
          <div key={index} className="flex items-center justify-between text-xs gap-4">
            <span className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full" style={{ backgroundColor: entry.color }} />
              {entry.name}
            </span>
            <span className="font-medium">{formatCurrency(entry.value)}</span>
          </div>
        ))}
      </div>
    </div>
  )
}

export default function AccountingPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [lastRefresh, setLastRefresh] = useState(null)
  const [isRefreshing, setIsRefreshing] = useState(false)

  const { currentClinicId } = useAuthStore()
  const isAggregateMode = currentClinicId === null

  const dateRange = useMemo(() => {
    const startParam = searchParams.get('start')
    const endParam = searchParams.get('end')
    if (startParam && endParam) {
      try {
        const from = parse(startParam, 'yyyy-MM-dd', new Date())
        const to = parse(endParam, 'yyyy-MM-dd', new Date())
        if (!isNaN(from.getTime()) && !isNaN(to.getTime())) return { from, to }
      } catch (e) {
        console.error('[ACCOUNTING] Error parsing date params:', e)
      }
    }
    return getDefaultDateRange()
  }, [searchParams])

  const setDateRange = useCallback((newRange) => {
    if (newRange?.from && newRange?.to) {
      setSearchParams(prev => {
        const params = new URLSearchParams(prev)
        params.set('start', format(newRange.from, 'yyyy-MM-dd'))
        params.set('end', format(newRange.to, 'yyyy-MM-dd'))
        return params
      })
    }
  }, [setSearchParams])

  const fetchData = useCallback(async (showRefreshIndicator = false) => {
    if (!dateRange?.from || !dateRange?.to) return
    try {
      if (showRefreshIndicator) setIsRefreshing(true)
      else setLoading(true)
      setError(null)

      const startDate = format(dateRange.from, 'yyyy-MM-dd')
      const endDate = format(dateRange.to, 'yyyy-MM-dd')

      const response = isAggregateMode
        ? await accountingAPI.getAggregatePharmacyCost({ startDate, endDate })
        : await accountingAPI.getPharmacyCost({ startDate, endDate })

      if (response.data?.success) {
        setData(response.data.data)
        setLastRefresh(new Date())
      } else {
        throw new Error(response.data?.error?.message || 'Failed to load pharmacy cost data')
      }
    } catch (err) {
      console.error('[ACCOUNTING] Error fetching data:', err)
      setError(err.response?.data?.error?.message || err.message || 'Failed to load pharmacy cost data')
    } finally {
      setLoading(false)
      setIsRefreshing(false)
    }
  }, [isAggregateMode, dateRange])

  useEffect(() => { fetchData() }, [fetchData])

  useEffect(() => {
    const interval = setInterval(() => fetchData(true), AUTO_REFRESH_INTERVAL)
    return () => clearInterval(interval)
  }, [fetchData])

  const pharmacies = data?.pharmacies || []
  const clinicBreakdown = data?.clinic_breakdown || []

  const chartData = (data?.daily || []).map(day => ({
    date: day.displayDate,
    cogs: day.cogs,
    shipping: day.shipping,
    cost: day.cost,
  }))

  const handleRefresh = () => fetchData(true)

  if (error && !data) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-bold">Cost per Pharmacy</h1>
        </div>
        <Card className="p-8">
          <div className="flex flex-col items-center justify-center text-center">
            <AlertCircle className="h-12 w-12 text-destructive mb-4" />
            <h3 className="text-lg font-semibold mb-2">Failed to Load Report</h3>
            <p className="text-muted-foreground mb-4">{error}</p>
            <Button onClick={() => fetchData()}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Retry
            </Button>
          </div>
        </Card>
      </div>
    )
  }

  const summary = data?.summary
  const maxCost = pharmacies.reduce((m, p) => Math.max(m, p.cost), 0)

  return (
    <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between gap-2">
        <div className="min-w-0">
          <h1 className="text-lg sm:text-2xl font-bold truncate">
            Cost per Pharmacy{isAggregateMode ? ' — All Brands' : ''}
          </h1>
          <p className="text-xs text-muted-foreground hidden sm:block">
            COGS + shipping by fulfilling pharmacy · routed orders only
            {lastRefresh && ` · ${lastRefresh.toLocaleTimeString()}`}
          </p>
        </div>
        <div className="flex items-center gap-1.5 sm:gap-3 shrink-0">
          <DateRangePicker dateRange={dateRange} onDateRangeChange={setDateRange} />
          <Button
            variant="outline"
            size="icon"
            onClick={handleRefresh}
            disabled={isRefreshing}
            className="h-8 w-8 sm:h-9 sm:w-9"
          >
            {isRefreshing ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
          </Button>
        </div>
      </div>

      <DataFreshnessWarning daily={data?.daily} dateRange={dateRange} loading={loading} />

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <SummaryCard
          title="Total Pharmacy Cost"
          value={formatCurrency(summary?.total_cost || 0)}
          subtitle="COGS + shipping"
          icon={DollarSign}
          loading={loading}
        />
        <SummaryCard
          title="Drug COGS"
          value={formatCurrency(summary?.total_cogs || 0)}
          subtitle="Curated drug price"
          icon={Package}
          loading={loading}
        />
        <SummaryCard
          title="Shipping"
          value={formatCurrency(summary?.total_shipping || 0)}
          subtitle={`${formatNumber(summary?.total_orders || 0)} routed orders`}
          icon={Truck}
          loading={loading}
        />
        <SummaryCard
          title="Pharmacies"
          value={formatNumber(summary?.pharmacy_count || 0)}
          subtitle={isAggregateMode ? `${formatNumber(data?.clinics_included?.length || 0)} clinics` : 'with routed spend'}
          icon={Building2}
          loading={loading}
        />
      </div>

      {/* Daily Spend Trend */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base font-medium">Daily Pharmacy Spend</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="h-[280px] flex items-center justify-center">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : chartData.length > 0 ? (
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                <XAxis
                  dataKey="date"
                  tick={{ fontSize: 12 }}
                  tickLine={false}
                  axisLine={false}
                  interval={Math.floor(chartData.length / 8)}
                />
                <YAxis
                  tick={{ fontSize: 12 }}
                  tickLine={false}
                  axisLine={false}
                  tickFormatter={(value) => formatCurrency(value, true)}
                />
                <Tooltip content={<CostTooltip />} />
                <Bar dataKey="cogs" name="COGS" stackId="cost" fill="hsl(var(--primary))" radius={[0, 0, 0, 0]} />
                <Bar dataKey="shipping" name="Shipping" stackId="cost" fill="hsl(var(--primary) / 0.4)" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[280px] flex items-center justify-center text-muted-foreground">
              No data available for selected period
            </div>
          )}
        </CardContent>
      </Card>

      {/* Per-Pharmacy Table */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base font-medium">Spend by Pharmacy</CardTitle>
          <p className="text-xs text-muted-foreground mt-1">
            Fulfilling pharmacy from pharmacy → script → routing. Sorted by total cost.
          </p>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="animate-pulse space-y-2">
              {[...Array(5)].map((_, i) => <div key={i} className="h-8 bg-muted rounded"></div>)}
            </div>
          ) : pharmacies.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2 px-2 font-medium">Pharmacy</th>
                    <th className="text-right py-2 px-2 font-medium">Total Cost</th>
                    <th className="text-right py-2 px-2 font-medium hidden sm:table-cell">COGS</th>
                    <th className="text-right py-2 px-2 font-medium hidden sm:table-cell">Shipping</th>
                    <th className="text-right py-2 px-2 font-medium">Orders</th>
                    <th className="text-right py-2 px-2 font-medium">$/Order</th>
                    {isAggregateMode && (
                      <th className="text-left py-2 px-2 font-medium hidden lg:table-cell">By Clinic</th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {pharmacies.map((p, index) => (
                    <tr key={index} className="border-b border-muted/50 hover:bg-muted/50">
                      <td className="py-2 px-2">
                        <div className="font-medium">{p.name}</div>
                        {/* Inline mini bar for relative cost */}
                        <div className="mt-1 h-1 bg-muted rounded-full overflow-hidden max-w-[160px]">
                          <div
                            className="h-full bg-primary rounded-full"
                            style={{ width: maxCost > 0 ? `${Math.max(2, (p.cost / maxCost) * 100)}%` : '0%' }}
                          />
                        </div>
                      </td>
                      <td className="text-right py-2 px-2 font-semibold">{formatCurrency(p.cost)}</td>
                      <td className="text-right py-2 px-2 text-muted-foreground hidden sm:table-cell">{formatCurrency(p.cogs)}</td>
                      <td className="text-right py-2 px-2 text-muted-foreground hidden sm:table-cell">{formatCurrency(p.shipping)}</td>
                      <td className="text-right py-2 px-2">{formatNumber(p.orders)}</td>
                      <td className="text-right py-2 px-2 text-muted-foreground">{formatCurrency(p.avg_cost_per_order)}</td>
                      {isAggregateMode && (
                        <td className="py-2 px-2 hidden lg:table-cell">
                          <div className="flex flex-wrap gap-1.5">
                            {Object.entries(p.by_clinic || {})
                              .sort((a, b) => b[1].cost - a[1].cost)
                              .map(([clinicId, info]) => (
                                <span
                                  key={clinicId}
                                  className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs bg-muted"
                                  title={`${info.displayName}: ${formatCurrency(info.cost)} · ${formatNumber(info.orders)} orders`}
                                >
                                  <span className="w-2 h-2 rounded-full" style={{ backgroundColor: info.color }} />
                                  {info.displayName}
                                  <span className="text-muted-foreground">{formatCurrency(info.cost, true)}</span>
                                </span>
                              ))}
                          </div>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t-2 font-semibold">
                    <td className="py-2 px-2">Total</td>
                    <td className="text-right py-2 px-2">{formatCurrency(summary?.total_cost || 0)}</td>
                    <td className="text-right py-2 px-2 hidden sm:table-cell">{formatCurrency(summary?.total_cogs || 0)}</td>
                    <td className="text-right py-2 px-2 hidden sm:table-cell">{formatCurrency(summary?.total_shipping || 0)}</td>
                    <td className="text-right py-2 px-2">{formatNumber(summary?.total_orders || 0)}</td>
                    <td className="text-right py-2 px-2">
                      {formatCurrency(summary?.total_orders > 0 ? Math.round(summary.total_cost / summary.total_orders) : 0)}
                    </td>
                    {isAggregateMode && <td className="hidden lg:table-cell" />}
                  </tr>
                </tfoot>
              </table>
            </div>
          ) : (
            <div className="py-8 text-center text-muted-foreground">
              No routed pharmacy spend for selected period
            </div>
          )}
        </CardContent>
      </Card>

      {/* Per-clinic rollup (aggregate mode) */}
      {isAggregateMode && clinicBreakdown.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base font-medium">Spend by Clinic</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {clinicBreakdown
                .slice()
                .sort((a, b) => b.summary.total_cost - a.summary.total_cost)
                .map(c => (
                  <div key={c.clinicId} className="border rounded-lg p-3 bg-muted/30">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: c.color }} />
                      <span className="font-medium text-sm">{c.displayName}</span>
                    </div>
                    <div className="text-xl font-bold">{formatCurrency(c.summary.total_cost)}</div>
                    <div className="text-xs text-muted-foreground mt-1">
                      {formatNumber(c.summary.total_orders)} orders · {formatNumber(c.summary.pharmacy_count)} pharmacies
                    </div>
                  </div>
                ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  )
}
